import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import {
  DateTimeAdapter,
  OWL_DATE_TIME_FORMATS,
  OWL_DATE_TIME_LOCALE,
  OwlDateTimeModule,
} from '@danielmoncada/angular-datetime-picker';
import { MomentDateTimeAdapter, OwlMomentDateTimeModule } from '@danielmoncada/angular-datetime-picker-moment-adapter';
import moment from 'moment';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { distinctUntilChanged, map, skip, take } from 'rxjs';

import { FieldTypeEnum } from '@models/module/fields/enums';
import {
  FilterDateOperators,
  FilterLogic,
  FilterNumberOperators,
  FilterOperator,
  FilterOperatorKey,
  FilterOperators,
  FilterTextOperators,
} from '@models/module/grid';
import {
  DataListCommandFilterType,
  DataListFilterFormGroup,
  DataListFilterType,
  FiltersToApply,
  LocalFilterModel,
} from '@shared/components/data-list/common';
import { DateTimeFormatService } from '@shared/components/fields/cipo-date-time/date-time-format.service';
import { DATE_TIME_FORMATS } from '@shared/consts';
import { CipoFormModule } from '@shared/modules/forms.module';
import { ItvDatePipe } from '@shared/pipes';
import { DataListFilterPipe } from '@shared/pipes/data-list-filters.pipe';

type RawFilter = {
  filter: DataListCommandFilterType;
  operator: FilterOperator;
  value: string | string[];
};

type FilterFormType = {
  [key: string]: FormGroup<DataListFilterFormGroup>;
};

@Component({
  selector: 'app-data-list-filters',
  imports: [
    MatExpansionModule,
    MatButtonToggleModule,
    OwlDateTimeModule,
    OwlMomentDateTimeModule,
    NgxSkeletonLoaderModule,
    DataListFilterPipe,
    CipoFormModule,
  ],
  providers: [
    { provide: DateTimeAdapter, useClass: MomentDateTimeAdapter, deps: [OWL_DATE_TIME_LOCALE] },
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: (dateTimeFormatService: DateTimeFormatService) => dateTimeFormatService.getFormats(),
      deps: [DateTimeFormatService],
    },
    {
      provide: OWL_DATE_TIME_LOCALE,
      useFactory: (dateTimeFormatService: DateTimeFormatService) => dateTimeFormatService.getLocale(),
      deps: [DateTimeFormatService],
    },
    ItvDatePipe,
  ],
  templateUrl: './data-list-filters.component.html',
  styleUrl: './data-list-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataListFiltersComponent {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly RANGE_FILTER_KEYS: FilterOperatorKey[] = ['Between', 'Last2Weeks', 'Last30Days'];

  filtersToApply = model<FiltersToApply>({});
  closeFilters = output();
  filters = input<DataListCommandFilterType[], DataListCommandFilterType[]>([], {
    transform: filters => {
      // add operators to filters based on type (if they don't have any)
      const mappedFilters = filters.map(filter => {
        if (!filter.operators?.length) {
          switch (filter.type) {
            case 'date':
              filter.operators = FilterDateOperators;
              break;
            case 'number':
              filter.operators = FilterNumberOperators;
              break;
            case 'bool':
              filter.operators = ['Equal'];
              break;
            default:
              if (filter.getValues || filter.values) {
                filter.operators = ['Equal'];
              } else {
                filter.operators = FilterTextOperators;
              }
              break;
          }
        }
        if (filter.operators.length === 1) {
          filter.selectedOperator = filter.operators[0];
        }
        return filter;
      });
      this.initForm(mappedFilters);
      return mappedFilters;
    },
  });

  filterLogic = signal(FilterLogic.AND);
  form = this.fb.group<FilterFormType>({});
  accordionClosed = signal(true);
  accordion = viewChild(MatAccordion);

  private filtersChangedLocally = false;
  private filtersToApply$ = toObservable(this.filtersToApply).pipe(
    map(filters => filters?.filters),
    distinctUntilChanged(),
    skip(1),
    takeUntilDestroyed(),
  );

  constructor() {
    this.filtersToApply$.subscribe(filters => {
      if (!this.filtersChangedLocally) {
        this.resetFilter();
        this.cdr.detectChanges();
        if (filters) {
          this.populateFormValues(filters);
        }
      } else {
        this.filtersChangedLocally = false;
      }
    });
  }

  initForm(filters: DataListCommandFilterType[]) {
    this.form = this.fb.group({});
    filters.forEach(filter => {
      this.form.addControl(
        filter.fieldId.toString(),
        this.fb.group({
          operator: this.fb.control(filter.selectedOperator, { nonNullable: true }),
          value: this.fb.control({ value: '', disabled: !filter.selectedOperator }, { nonNullable: true }),
        }),
      );
    });
  }

  private populateFormValues({ logic, filterList }: LocalFilterModel) {
    this.filterLogic.set(logic ?? FilterLogic.AND);
    filterList.forEach(({ field, value, operator, dataSource }) => {
      const filterControls = this.form.controls[field].controls;
      filterControls.operator.setValue(FilterOperator[operator] as keyof typeof FilterOperator);
      filterControls.value.enable();
      if (dataSource) {
        const filter = this.filters().find(({ fieldId }) => fieldId == field);
        this.loadValues(filter);
      }
      filterControls.value.setValue(value as string);
    });
  }

  selectOperator(filter: DataListCommandFilterType) {
    const filterFormGroup = this.form.controls[filter.fieldId].controls;
    filterFormGroup.value.enable();
    filterFormGroup.value.reset();

    if (filterFormGroup.operator.value === 'Last30Days') {
      // The following type assertion is needed to save the value inside formControl.
      filterFormGroup.value.setValue([
        moment().subtract(30, 'days').toISOString(),
        moment().toISOString(),
      ] as unknown as string);
    } else if (filterFormGroup.operator.value === 'Last2Weeks') {
      filterFormGroup.value.setValue([
        moment().subtract(14, 'days').toISOString(),
        moment().toISOString(),
      ] as unknown as string);
    }
  }

  loadValues(filter: DataListCommandFilterType) {
    if (filter.getValues && !filter.values) {
      filter.getValues.pipe(take(1)).subscribe(v => {
        this.filters().find(({ fieldId }) => fieldId === filter.fieldId).values = v;
        this.cdr.markForCheck();
      });
    }
  }

  closeFilter() {
    this.closeFilters.emit();
  }

  resetFilter(controlName?: number | string) {
    if (controlName) {
      this.resetSingleFilter(this.form.controls[controlName]);
    } else {
      Object.values(this.form.controls).forEach(control => this.resetSingleFilter(control));
    }
  }

  private resetSingleFilter(control?: FormGroup<DataListFilterFormGroup>) {
    if (!control) {
      return;
    }
    control.reset();
    if (control.controls.operator.value) {
      control.controls.value.enable();
    } else {
      control.controls.value.disable();
    }
  }

  toggleAll() {
    if (this.accordionClosed()) {
      this.accordion().openAll();
    } else {
      this.accordion().closeAll();
    }

    this.accordionClosed.update(val => !val);
  }

  applyFilters() {
    const formData = this.form.value as any;
    for (const key in formData) {
      const value = formData[key].value;
      if (!value) continue;
      if (moment.isMoment(value)) {
        formData[key].value = moment(formData[key].value).format(DATE_TIME_FORMATS.dateIso);
      } else if (moment.isMoment(value[0])) {
        formData[key].value = formData[key].value.map((val: Date) => moment(val).format(DATE_TIME_FORMATS.dateIso));
      }
    }
    const formKeys = Object.keys(this.form.controls).filter(key => {
      const val = formData[key].value;
      switch (typeof val) {
        case 'number':
          return !(isNaN(val) || val === null || val === undefined);
        case 'string':
          return !!val;
        case 'boolean':
          return true;
        default:
          if (val instanceof Date) {
            return true;
          }
          return !!val?.length;
      }
    });

    if (formKeys.length === 0) {
      this.filtersToApply.update(({ criteria }) => ({ criteria }));
      return;
    }

    const rawFilters: RawFilter[] = formKeys.map(key => ({
      filter: this.filters().find(f => f.fieldId == key),
      operator: FilterOperators[formData[key].operator],
      value: formData[key].value,
    }));

    const filtersToApply: FiltersToApply = {
      filters: {
        logic: this.filterLogic(),
        filterList: rawFilters.map(({ filter, operator, value }) => ({
          field: filter.fieldId.toString(),
          fieldTypeId: this.getFieldTypeId(filter.type),
          operator,
          value,
        })),
      },
    };
    this.filtersChangedLocally = true;
    this.filtersToApply.update(({ criteria }) => ({ criteria, ...filtersToApply }));
    this.closeFilter();
  }

  private getFieldTypeId(type: DataListFilterType): FieldTypeEnum | undefined {
    switch (type) {
      case 'date':
        return FieldTypeEnum.Date;
      case 'number':
        return FieldTypeEnum.Number;
      case 'bool':
        return FieldTypeEnum.YesNo;
      case 'text':
        return FieldTypeEnum.Text;
      default:
        return undefined;
    }
  }
}
